import { useCallback } from 'react';
import { PackageLine } from '@freight-cbm/shared-types';
import { useAppStore } from '@/store/store';
import { ScrubInput, UnitToggle } from 'calc-controls';
import { UnitWarning } from './UnitWarning';
import { dimUnitOptions, weightUnitOptions } from './UnitSelect';
import { CopyPlus, X } from 'lucide-react';

interface PackageRowProps {
  pkg: PackageLine;
  index: number;
}

export function PackageRow({ pkg, index }: PackageRowProps) {
  const updatePackage = useAppStore((s) => s.updatePackage);
  const removePackage = useAppStore((s) => s.removePackage);
  const duplicatePackage = useAppStore((s) => s.duplicatePackage);
  const totalPackages = useAppStore((s) => s.packages.length);

  const handleFieldChange = useCallback((field: keyof PackageLine, value: string | number) => {
    updatePackage(pkg.id, field, value as never);
  }, [updatePackage, pkg.id]);

  const cellStyle: React.CSSProperties = {
    padding: 'var(--space-1)',
    minWidth: 60,
  };

  return (
    <>
      <tr data-testid={`package-row-${index}`}>
      <td style={cellStyle}>
        <ScrubInput
          value={pkg.length}
          onChange={(v) => handleFieldChange('length', v)}
          min={0}
          step={1}
          decimals={2}
          inline
        />
      </td>
      <td style={cellStyle}>
        <ScrubInput
          value={pkg.width}
          onChange={(v) => handleFieldChange('width', v)}
          min={0}
          step={1}
          decimals={2}
          inline
        />
      </td>
      <td style={cellStyle}>
        <ScrubInput
          value={pkg.height}
          onChange={(v) => handleFieldChange('height', v)}
          min={0}
          step={1}
          decimals={2}
          inline
        />
      </td>
      <td style={cellStyle}>
        <UnitToggle
          options={dimUnitOptions}
          value={pkg.dimUnit}
          onChange={(v) => handleFieldChange('dimUnit', v)}
        />
      </td>
      <td style={cellStyle}>
        <ScrubInput
          value={pkg.weight}
          onChange={(v) => handleFieldChange('weight', v)}
          min={0}
          step={1}
          decimals={2}
          inline
        />
      </td>
      <td style={cellStyle}>
        <UnitToggle
          options={weightUnitOptions}
          value={pkg.weightUnit}
          onChange={(v) => handleFieldChange('weightUnit', v)}
        />
      </td>
      <td style={cellStyle}>
        <ScrubInput
          value={pkg.quantity}
          onChange={(v) => handleFieldChange('quantity', v)}
          min={1}
          step={1}
          decimals={0}
          inline
        />
      </td>
      <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
        <button
          className="btn btn-sm"
          onClick={() => duplicatePackage(pkg.id)}
          aria-label={`Duplicate package ${index + 1}`}
          title="Duplicate"
        >
          <CopyPlus size={14} />
        </button>
        {totalPackages > 1 && (
          <button
            className="btn btn-sm"
            onClick={() => removePackage(pkg.id)}
            aria-label={`Remove package ${index + 1}`}
            title="Remove"
            style={{ marginLeft: 'var(--space-1)' }}
          >
            <X size={14} />
          </button>
        )}
      </td>
    </tr>
    {<tr><td colSpan={8}><UnitWarning pkg={pkg} /></td></tr>}
    </>
  );
}
